/**
 * @file scripts/pages/login.jsx
 * @description
 * This is the login page for the Finch for Text mini-site. It provides the
 * basic means for logging in, as expected, and also contains any rendering
 * logic for the various contextual states the login page contains.
 */
import React, {Component} from 'react';
import history from '../utils/history';
import session from '../utils/session';
import settings from '../utils/settings';
import FinchButton from '../components/demo/FinchButton';
import urlHandler from '../utils/urlHandler';
import ErrorMessages from '../utils/error-messages';
import TermsOfService from '../styles/docs/FinchComputing_TermsOfService.pdf';
import { BroadcastChannel } from 'broadcast-channel';
import miscUtils from '../utils/miscUtils';

import azure_image from '../styles/images/auth_azure.png';
import google_image from '../styles/images/auth_google.png';
import github_image from '../styles/images/auth_github.png';
import facebook_image from '../styles/images/auth_facebook.png';
import linkedin_image from '../styles/images/auth_linkedin.png';
import okta_image from '../styles/images/auth_okta.png';
import lock_image from '../styles/images/auth_lock.png';

class Login extends Component {
    constructor(props){
        super(props);

        this.validate = this.validate.bind(this);
        this.processFFTLogin = this.processFFTLogin.bind(this);
        this.finalizeLogin = this.finalizeLogin.bind(this);
        this.processProviderLogin = this.processProviderLogin.bind(this);

        this.state =  {
            hasErrors: false,
            message: '',
            isAuthenticating: false,
            warning: true,
            submit: this.validate,
            login: true,
            processing: false,
            endprocessing: 'false',
            providers: []
        };

        this.providerImages = {
            azure: azure_image,
            google: google_image,
            github: github_image,
            facebook: facebook_image,
            linkedin: linkedin_image,
            okta: okta_image,
            default: lock_image
        }

        this.channel = null;
    }

    /**
     * @function componentDidMount
     * @description Called when component is mounted
     */
    componentDidMount() {

        this.channel = new BroadcastChannel('fft_minisite');
        this.channel.addEventListener('message', (msg) => {
            if (msg && 'type' in msg) {
                if (msg.type === 'login:provider') {
                    session.start(msg.data.token, msg.data.userId);
                    this.finalizeLogin();
                }
            }
        });

        // Lookup the authentication providers that are supported
        urlHandler.authenticateGetProviders((result, status) => {
            if (status === 200) {
                this.setState({
                    providers: result.providers && result.providers.length > 0 ? result.providers.filter((prov) => { return prov.enabled }) : []
                });
            } else {
                this.setState({
                    providers: []
                });
            }
        })                
    }

    componentWillUnmount() {
        if (this.channel) {
            this.channel.close();
        }
    }

    // Our form submission process, which takes the user/pass combo from the
    // component state and sends them to the API for validation. On success,
    // a token is returned and handled appropriately.
    validate(e) {
        const user = this.refs.username.value;
        const pass  = this.refs.password.value;
        const stateObj = {};

        e.preventDefault();

        // do some basic validate on the user/pass values
        if ((user.length < 3) || (pass.length < 3)) {

            stateObj.hasErrors = true;
            stateObj.message = (user.length < 3) ?
                'Please enter your email address.' :
                'Please enter a password.';

            this.setState(stateObj);
            return false;
        }

        // make sure user has accepted Terms of Service 
        const tos = this.refs.tos; 
        if (!tos.checked) { 
            stateObj.hasErrors = true; 
            stateObj.message = 'Must accept terms of service.' 
            this.setState(stateObj); 
            return false; 
        }

        // default our error state
        this.setState({
            hasErrors: false,
            message: '',
            isAuthenticating: true
        });

        urlHandler.sendRequest('authenticate', null, 'Basic ' + window.btoa(user + ':' + pass), null, null, this.processFFTLogin)
    }

    processFFTLogin(response){

        if(response.error_status === 401) {
            this.setState({
                hasErrors: true,
                message: ErrorMessages.INVALID_CREDENTIALS,
                isAuthenticating: false,
                processing: false
            })
        } else if(response.error_status) {
            history.push('/error/' + response.error_status + '/' + response.error_message);
        } else {
            session.start(response.access_token, this.refs.username.value);
            this.finalizeLogin();
        }
    }

    /**
     * @function finalizeLogin
     * @description Finalize login after session has started
     */
    finalizeLogin() {

        // Broadcast login to other tabs
        var channel = new BroadcastChannel('fft_minisite');
        channel.postMessage({
            type: 'login',
            user: session.getUserName()
        });
        channel.close();
        settings.setValue('forceEnglish', true);

        // After session has been started, get user permissions and cache them.
        urlHandler.getUserPermissions((response, perms, status) => {
            if (status != 200) {
                settings.setValue('permissions', null);
                history.push('/error/' + status + '/Could not get user permissions');
            } else {
                settings.setValue('permissions', perms);
                if(perms && perms.features && perms.features.includes('InformationExtraction') && perms.endpoints.includes('/api/v1/information-extraction-prompts')){
                    urlHandler.getIETemplates((response, informationExtractionTemplates, status) => {
                        if (status != 200) {
                            console.log('Error calling getIETemplates => ' + JSON.stringify(response));
                            settings.setValue('informationExtractionTemplates', null);
                        } else  {
                            settings.setValue('informationExtractionTemplates', informationExtractionTemplates);
                        }
                    });
                }
                history.push('/');
            }
        });

        // Check recent docs length against the env variable and trim if necessary
        var maxDocs = window._maxRecentDocs - 1;
        var recentDocs = JSON.parse(localStorage.getItem('recentDocuments'));
        if (recentDocs) {
            if (recentDocs.length > maxDocs) {
                recentDocs.splice(0, recentDocs.length - window._maxRecentDocs);
                localStorage.setItem('recentDocuments', JSON.stringify(recentDocs));
            }
        } else {
            localStorage.setItem('recentDocuments', JSON.stringify([]));
        }
    }

    /**
     * @function processProviderLogin
     * @description Validate and initiate the provider login process
     * @param {object} provider - provider information
     */
    processProviderLogin(provider) {

        // make sure user has accepted Terms of Service 
        const tos = this.refs.tos; 
        if (!tos.checked) { 
            this.setState({
                hasErrors: true,
                message: 'Must accept terms of service.'
            }); 
        } else {
            // Contact provider to get initial login information
            urlHandler.authenticateWithProvider(provider.protocol, provider.name, (result, status) => {
                if (status === 200) {
                    if (result.url) {                    
                        let authWindow = miscUtils.centeredPopupWindow(result.url, '_blank', window, 700, 500);
                        if (authWindow) {
                            if (window.focus) { authWindow.focus() }
                        }
                    } else {
                        this.setState({
                            hasErrors: true,
                            message: 'No provider URL in provider response.'
                        });
                    }
                }
            })  
        }              
    }
    
    render() {

        if (this.state.isAuthenticating) {
            this.state.processing  = true
        }

        return (
            <div className='login-wrapper'>
                <div className="login-form">
                    <div className="logo"></div>
                    {/* <div className="join-message">
                        If you do not already have an account, click <RequestAccountDialog text="here" renderIcon={false}/> to request to join as a beta user.
                    </div> */}
                    {this.state.hasErrors &&
                        <div className="login-error">
                            {this.state.message}
                        </div>
                    }
                    <form name='auth_form' action='#' className='auth-form'>
                        {this.state.providers.length == 0 &&
                            <div className='form-field-wrapper text-field'>
                                <input type='text'
                                        className='form-field username'
                                        name='username'
                                        id='usernameField'
                                        ref='username'
                                        placeholder='Username'
                                        autoFocus
                                />
                            </div>
                        }
                        {this.state.providers.length == 0 &&
                            <div className='form-field-wrapper password-field'>
                                <input type='password'
                                        className='form-field password'
                                        name='password'
                                        id='passwordField'
                                        placeholder='Password'
                                        ref='password'
                                />
                            </div>
                        }
                        <div className='form-field-wrapper tos-wrapper'> 
                            <label htmlFor='tosField' className='form-label'>Terms Of Service</label> 
                            <table> 
                                <tbody> 
                                    <tr> 
                                        <td className="tos-check"> 
                                            <input type='checkbox' 
                                                name='tos' 
                                                id='tosField' 
                                                ref='tos' 
                                            /> 
                                        </td> 
                                        <td className="tos-text"> 
                                            You must accept the Finch Computing Terms of Service to login.  
                                            Click <a href={ TermsOfService } target="__blank" download="FinchComputing_TermsOfService.pdf">here</a> to  
                                            download your copy. 
                                        </td> 
                                    </tr> 
                                </tbody> 
                            </table> 
                        </div>
                        {this.state.providers.length == 0 && 
                            <div>
                                <FinchButton
                                    primary = {true}
                                    text = 'Log In'
                                    warning = {this.state.warning}
                                    processing = {this.state.processing}
                                    submit={this.state.submit}>
                                </FinchButton>
                            </div>
                        }
                        {this.state.providers.length > 0 &&
                            <div className="provider-wrapper">
                                <div className="hr-sect">login providers</div>
                                <div className="form-field-wrapper text-field providers">
                                    {this.state.providers.map((provider, indx) => {
                                        let image = this.providerImages.default;
                                        if (provider.name in this.providerImages) {
                                            image = this.providerImages[provider.name];
                                        }
                                        return <button 
                                            key={`provider_button_${indx}`}
                                            type="button" 
                                            className="provider-button"
                                            title={`Login with ${provider.name} via ${provider.protocol}`}
                                            onClick={(e) => { return this.processProviderLogin(provider) }}>
                                                {image && <img src={image} className="provider-icon"></img>}
                                                {provider.name && String(provider.name[0]).toUpperCase() + String(provider.name).slice(1)}
                                        </button>
                                    })}
                                </div>
                            </div>
                        }
                    </form>
                </div>
            </div>
        );
    }
}

export default Login;